<template>
  <div class="container">
    <div class="lang-toggle">
      <button :class="{ active: lang === 'en' }" @click="setLang('en')">
        EN
      </button>
      <button :class="{ active: lang === 'pt' }" @click="setLang('pt')">
        PT
      </button>
    </div>

    <!-- English Version -->
    <div v-show="lang === 'en'" data-lang="en">
      <h1>Privacy Policy – Sistema de Postal Móvel</h1>
      <p><strong>Last Updated:</strong> July 15, 2025</p>
      <p><strong>Responsible Company:</strong> Portador Diário</p>

      <h2>1. Introduction</h2>
      <p>
        Portador Diário respects your privacy and is committed to protecting the
        personal data of users of the
        <strong>Sistema de Postal Móvel</strong> application, both the
        <strong>Client</strong> and <strong>Partner</strong> versions.
      </p>

      <h2>2. Data We Collect</h2>
      <ul>
        <li>
          <strong>Personal Data:</strong> Full name, address, NUIT number,
          company declaration, vehicle registration and photos
        </li>
        <li><strong>Location Data:</strong> Real-time geolocation</li>
        <li>
          <strong>Camera:</strong> Capturing photos of documents and vehicles
        </li>
        <li><strong>Storage:</strong> Temporary file saving</li>
        <li><strong>Technical Data:</strong> Device type, OS, usage logs</li>
      </ul>

      <h2>3. Purpose of Collection</h2>
      <ul>
        <li>Verification of clients and partners</li>
        <li>Facilitation of deliveries</li>
        <li>Security and tracking of services</li>
        <li>Legal compliance</li>
        <li>Improvement of user experience</li>
      </ul>

      <h2>4. Data Sharing</h2>
      <ul>
        <li>
          <strong>Supabase:</strong> Authentication, storage, and database
        </li>
        <li>Authorized partners for delivery</li>
        <li>Legal authorities when required</li>
      </ul>

      <h2>5. Device Permissions</h2>
      <ul>
        <li>
          <strong>Camera:</strong> Capturing photos of documents and vehicles
        </li>
        <li><strong>Location:</strong> Real-time tracking</li>
        <li><strong>Storage:</strong> Temporary file saving</li>
      </ul>

      <h2>6. Data Security</h2>
      <p>
        We apply secure connections (HTTPS), encryption, and authentication via
        Supabase.
      </p>

      <h2>7. Data Retention</h2>
      <p>
        Data is retained as long as necessary and may be deleted upon request.
      </p>

      <h2>8. User Rights</h2>
      <ul>
        <li>Access, correct, or delete data</li>
        <li>Withdraw consent</li>
        <li>
          Contact:
          <a href="mailto:privacidade@portadordiario.co.mz"
            >privacidade@portadordiario.co.mz</a
          >
        </li>
      </ul>

      <h2>9. Children's Privacy</h2>
      <p>
        Not intended for children under 13. We do not collect data from
        children.
      </p>

      <h2>10. Policy Updates</h2>
      <p>
        This policy may be updated and will always be available in the app or on
        the website.
      </p>

      <h2>11. Contact</h2>
      <p>
        Company: Portador Diário<br />
        Email:
        <a href="mailto:privacidade@portadordiario.co.mz"
          >privacidade@portadordiario.co.mz</a
        ><br />
        Address: Maputo, Mozambique
      </p>

      <h2>12. Acceptance</h2>
      <p>
        By using the Sistema de Postal Móvel app, you agree to this Privacy
        Policy.
      </p>
    </div>

    <!-- Portuguese Version -->
    <div v-show="lang === 'pt'" data-lang="pt">
      <h1>Política de Privacidade – Sistema de Postal Móvel</h1>
      <p><strong>Última atualização:</strong> 15 de Julho de 2025</p>
      <p><strong>Empresa responsável:</strong> Portador Diário</p>

      <h2>1. Introdução</h2>
      <p>
        A Portador Diário respeita a sua privacidade e está comprometida com a
        proteção dos dados pessoais dos utilizadores da aplicação
        <strong>Sistema de Postal Móvel</strong>, tanto na versão
        <strong>Cliente</strong> quanto na versão <strong>Parceiro</strong>.
      </p>

      <h2>2. Dados que coletamos</h2>
      <ul>
        <li>
          <strong>Dados pessoais:</strong> Nome completo, endereço, número do
          NUIT, declaração da empresa, livrete e fotografias do veículo
        </li>
        <li>
          <strong>Dados de localização:</strong> Geolocalização em tempo real
        </li>
        <li>
          <strong>Câmera:</strong> Captura de fotos de documentos e viaturas
        </li>
        <li>
          <strong>Armazenamento:</strong> Salvamento temporário de arquivos
        </li>
        <li>
          <strong>Dados técnicos:</strong> Tipo de dispositivo, sistema
          operacional, registros de uso
        </li>
      </ul>

      <h2>3. Finalidade da coleta</h2>
      <ul>
        <li>Verificação de clientes e parceiros</li>
        <li>Facilitação de entregas</li>
        <li>Segurança e rastreamento dos serviços</li>
        <li>Conformidade legal</li>
        <li>Melhoria da experiência do usuário</li>
      </ul>

      <h2>4. Compartilhamento de dados</h2>
      <ul>
        <li>
          <strong>Supabase:</strong> Autenticação, armazenamento e banco de
          dados
        </li>
        <li>Parceiros autorizados para entrega</li>
        <li>Autoridades legais quando exigido</li>
      </ul>

      <h2>5. Permissões do dispositivo</h2>
      <ul>
        <li>
          <strong>Câmera:</strong> Captura de fotos de documentos e viaturas
        </li>
        <li><strong>Localização:</strong> Rastreamento em tempo real</li>
        <li><strong>Armazenamento:</strong> Salvamento temporário</li>
      </ul>

      <h2>6. Segurança dos dados</h2>
      <p>
        Aplicamos conexões seguras (HTTPS), criptografia e autenticação via
        Supabase.
      </p>

      <h2>7. Retenção de dados</h2>
      <p>
        Os dados são mantidos enquanto forem necessários e podem ser excluídos
        mediante solicitação.
      </p>

      <h2>8. Direitos do utilizador</h2>
      <ul>
        <li>Acessar, corrigir ou excluir dados</li>
        <li>Retirar consentimento</li>
        <li>
          Contato:
          <a href="mailto:privacidade@portadordiario.co.mz"
            >privacidade@portadordiario.co.mz</a
          >
        </li>
      </ul>

      <h2>9. Privacidade de menores</h2>
      <p>
        Não destinado a menores de 13 anos. Não coletamos dados de crianças.
      </p>

      <h2>10. Atualizações da política</h2>
      <p>
        Esta política pode ser atualizada e estará sempre disponível no app ou
        site.
      </p>

      <h2>11. Contato</h2>
      <p>
        Empresa: Portador Diário<br />
        Email:
        <a href="mailto:privacidade@portadordiario.co.mz"
          >privacidade@portadordiario.co.mz</a
        ><br />
        Endereço: Maputo, Moçambique
      </p>

      <h2>12. Aceitação</h2>
      <p>
        Ao usar o app Sistema de Postal Móvel, você concorda com esta Política
        de Privacidade.
      </p>
    </div>
  </div>

  <!-- Scroll to top button -->
  <button id="topBtn" v-show="showTopButton" @click="scrollToTop">↑</button>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const lang = ref<'en' | 'pt'>('en');
const showTopButton = ref(false);

const setLang = (newLang: 'en' | 'pt') => {
  lang.value = newLang;
  localStorage.setItem('preferredLang', newLang);
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleScroll = () => {
  showTopButton.value = window.scrollY > 200;
};

onMounted(() => {
  const savedLang = localStorage.getItem('preferredLang') as 'en' | 'pt';
  if (savedLang === 'pt' || savedLang === 'en') lang.value = savedLang;

  window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
body {
  font-family: Verdana, 'Segoe UI', Tahoma, Geneva, sans-serif;
  background-color: #f9fafb;
  line-height: 1.6;
  color: #111827;
  margin: 0;
  padding: 0;
}
.container {
  max-width: 65vw;
  margin: 40px auto;
  background: #ffffff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
h1,
h2 {
  color: #1f2937;
}
h1 {
  font-size: 2em;
  margin-bottom: 20px;
}
h2 {
  margin-top: 30px;
  font-size: 1.4em;
}
ul {
  margin: 0;
  padding-left: 20px;
}
a {
  color: #7c3aed;
  text-decoration: none;
}
a:hover {
  text-decoration: underline;
}
.lang-toggle {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}
.lang-toggle button {
  background-color: #ede9fe;
  color: #6b21a8;
  border: none;
  padding: 6px 12px;
  margin-left: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}
.lang-toggle button.active {
  background-color: #7c3aed;
  color: white;
}
#topBtn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background-color: #7c3aed;
  color: white;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  cursor: pointer;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}
#topBtn:hover {
  background-color: #6d28d9;
}
@media (prefers-color-scheme: dark) {
  body {
    background-color: #111827;
    color: #f9fafb;
  }
  .container {
    background: #1f2937;
  }
  h1,
  h2 {
    color: #f3f4f6;
  }
  .lang-toggle button {
    background-color: #4c1d95;
    color: #ede9fe;
  }
  .lang-toggle button.active {
    background-color: #a78bfa;
    color: #1e1b4b;
  }
}
</style>
